import { Link } from "react-router-dom";

export interface ServiceItem {
  icon: string;
  title: string;
  desc: string;
  img: string;
  link?: string;
}

const defaultServices: ServiceItem[] = [
  {
    icon: "ri-home-6-line",
    title: "Custom Home Building",
    desc: "From foundation to finish, we build custom homes tailored to your vision, lifestyle, and budget with expert craftsmanship and quality materials throughout Colorado Springs.",
    img: "https://public.readdy.ai/ai/img_res/18e96cc4f2d1b9b631cdac840af2c713.jpg",
    link: "/services",
  },
  {
    icon: "ri-tools-line",
    title: "Home Remodeling & Renovations",
    desc: "Transform existing spaces with full-service remodeling — kitchens, bathrooms, basements, and whole-home renovations executed with precision and premium materials.",
    img: "https://readdy.ai/api/search-image?query=beautiful%20modern%20kitchen%20remodel%20renovation%20warm%20wood%20cabinetry%20stone%20countertops%20pendant%20lights%20open%20concept%20professional%20interior%20photography%20natural%20light%20wide%20angle%20completed%20project&width=600&height=400&seq=westec-svc-v2-02&orientation=landscape",
    link: "/services",
  },
  {
    icon: "ri-building-3-line",
    title: "Commercial Construction",
    desc: "Professional commercial builds and build-outs for offices, retail, and industrial spaces that meet your business timeline, budget, and quality requirements.",
    img: "https://readdy.ai/api/search-image?query=modern%20commercial%20office%20building%20construction%20exterior%20professional%20clean%20architecture%20glass%20steel%20warm%20afternoon%20sunlight%20wide%20angle%20business%20environment%20premium%20quality%20build%20Colorado&width=600&height=400&seq=westec-svc-v2-03&orientation=landscape",
    link: "/services",
  },
  {
    icon: "ri-layout-right-2-line",
    title: "Additions & Expansions",
    desc: "Add living space, a second story, or a garage addition that blends seamlessly with your existing structure and matches your architectural style.",
    img: "https://readdy.ai/api/search-image?query=home%20room%20addition%20construction%20second%20story%20expansion%20residential%20exterior%20warm%20stone%20siding%20seamless%20integration%20professional%20photography%20wide%20angle%20afternoon%20Colorado%20clear%20sky%20quality%20build&width=600&height=400&seq=westec-svc-v2-04&orientation=landscape",
    link: "/services",
  },
  {
    icon: "ri-paint-brush-line",
    title: "Interior & Exterior Upgrades",
    desc: "Refresh and modernize your property with premium interior finishes, exterior siding, roofing, windows, and upgrades that boost curb appeal and long-term value.",
    img: "https://readdy.ai/api/search-image?query=beautiful%20home%20exterior%20renovation%20upgrade%20modern%20siding%20fresh%20windows%20landscaping%20curb%20appeal%20professional%20photography%20warm%20afternoon%20sunlight%20residential%20Colorado%20wide%20angle%20quality%20craftsmanship&width=600&height=400&seq=westec-svc-v2-05&orientation=landscape",
    link: "/services",
  },
];

interface ServicesOverviewProps {
  services?: ServiceItem[];
  sectionLabel?: string;
  title?: string;
  subtitle?: string;
  showViewAll?: boolean;
  showPostCTA?: boolean;
  limit?: number;
}

export default function ServicesOverview({
  services,
  sectionLabel,
  title,
  subtitle,
  showViewAll = true,
  showPostCTA = false,
  limit,
}: ServicesOverviewProps) {
  const source = services ?? defaultServices;
  const displayed = limit ? source.slice(0, limit) : source;

  return (
    <section className="py-24 bg-brand-warm-mid">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div className="max-w-xl">
            <p className="text-xs font-body text-brand-amber uppercase tracking-widest font-semibold mb-3">
              {sectionLabel ?? "Our Services"}
            </p>
            <h2 className="font-heading font-bold text-4xl lg:text-5xl text-brand-dark leading-tight">
              {title ?? (
                <>
                  Construction &amp;<br />Remodeling Services
                </>
              )}
            </h2>
            {subtitle && (
              <p className="font-body text-brand-charcoal text-sm leading-relaxed mt-4">{subtitle}</p>
            )}
          </div>
          {showViewAll && (
            <Link
              to="/services"
              className="inline-flex items-center gap-2 border border-brand-warm-deep text-brand-dark px-6 py-3 rounded-full text-sm font-body font-medium hover:bg-white hover:border-brand-amber transition-colors cursor-pointer whitespace-nowrap self-start md:self-end"
            >
              View All Services
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line" />
              </span>
            </Link>
          )}
        </div>

        {/* Cards grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
          {displayed.map((svc) => (
            <article
              key={svc.title}
              className="bg-white rounded-2xl overflow-hidden border border-brand-warm-deep hover:border-brand-amber/40 transition-all group"
            >
              <div className="w-full h-52 overflow-hidden">
                <img
                  src={svc.img}
                  alt={`${svc.title} — Westec Builders Colorado Springs`}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-6">
                <div className="w-10 h-10 flex items-center justify-center bg-brand-warm-mid rounded-lg mb-4">
                  <i className={`${svc.icon} text-xl text-brand-amber`} />
                </div>
                <h3 className="font-heading font-semibold text-lg text-brand-dark mb-2">{svc.title}</h3>
                <p className="font-body text-sm text-brand-charcoal leading-relaxed mb-4">{svc.desc}</p>
                <Link
                  to={svc.link ?? "/services"}
                  className="inline-flex items-center gap-1 text-brand-amber text-sm font-body font-semibold hover:gap-2 transition-all cursor-pointer"
                >
                  Learn More
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line text-sm" />
                  </span>
                </Link>
              </div>
            </article>
          ))}
        </div>

        {/* Post-section CTA strip */}
        {showPostCTA && (
          <div className="bg-white rounded-2xl border border-brand-warm-deep p-8 flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <p className="font-heading font-bold text-xl text-brand-dark mb-1">
                Ready to Discuss Your Project?
              </p>
              <p className="font-body text-sm text-brand-charcoal">
                Get a free, no-obligation estimate from Colorado Springs&apos; trusted construction experts.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
              <Link
                to="/#estimate"
                className="bg-brand-amber text-white px-6 py-3 rounded-md text-sm font-body font-semibold hover:bg-brand-amber-light transition-colors cursor-pointer whitespace-nowrap text-center"
              >
                Get a Free Estimate
              </Link>
              <a
                href="tel:+17195550100"
                className="border border-brand-warm-deep text-brand-dark px-6 py-3 rounded-md text-sm font-body font-semibold hover:border-brand-amber transition-colors cursor-pointer whitespace-nowrap text-center"
              >
                Call (719) 555-0100
              </a>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
